import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "../utils/api";
import { usePageTitle } from "../hooks/usePageTitle";
import {
	formatJapaneseDateWithWeekday,
	formatJapaneseTime,
} from "../utils/dateUtils";

// イベントデータの型定義を更新
interface Event {
	id: number;
	title: string;
	slug: string;
	date: string;
	time: string;
	venue_name: string;
	prefecture: string;
	city: string;
	image_url: string;
	hashtags?: string[];
	status: string;
	ticket_types: {
		id: number;
		name: string;
		price: number;
		is_free: boolean;
	}[];
	opening_times?: {
		id: number;
		open_time: string;
		start_time: string;
		end_time: string;
		description?: string;
	}[];
}

const EventListPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [events, setEvents] = useState<Event[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [totalPages, setTotalPages] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);

	usePageTitle("イベント一覧");

	// 検索パラメータの取得
	const keyword = searchParams.get("keyword") || "";
	const category = searchParams.get("category") || "";
	const sort = searchParams.get("sort") || "date-asc";
	const page = parseInt(searchParams.get("page") || "1");

	// 表示カテゴリ（バックエンドで対応カテゴリがある場合に更新）
	const categories = ["すべて", "ロック", "ポップス", "ジャズ", "クラシック"];

	// ソートオプション
	const sortOptions = [
		{ value: "date-asc", label: "開催日（近い順）" },
		{ value: "date-desc", label: "開催日（遠い順）" },
		{ value: "price-asc", label: "価格（安い順）" },
		{ value: "price-desc", label: "価格（高い順）" },
	];

	// イベントデータの取得
	useEffect(() => {
		const fetchEvents = async () => {
			try {
				setIsLoading(true);

				// 検索クエリパラメータの構築
				const params = new URLSearchParams();

				if (keyword) {
					params.append("search", keyword);
				}

				if (category && category !== "すべて") {
					params.append("category", category);
				}

				// ソートパラメータの変換
				if (sort) {
					const [field, direction] = sort.split("-");
					params.append("sort_by", field);
					params.append("sort_direction", direction);
				}

				// ページネーション
				params.append("page", page.toString());
				params.append("perPage", "9"); // 1ページあたりの表示数

				// APIリクエスト
				const response = await apiClient.get(`/events?${params.toString()}`);

				// レスポンスデータの処理
				if (response.data) {
					setEvents(response.data.data || []);
					setTotalPages(response.data.last_page || 1);
					setCurrentPage(response.data.current_page || 1);
				} else {
					throw new Error("イベントデータの取得に失敗しました");
				}

				setIsLoading(false);
				setError(null);
			} catch (error) {
				console.error("イベントデータの取得に失敗しました:", error);
				setError("イベントデータの取得に失敗しました");
				setIsLoading(false);

				// エラー時はダミーデータを表示（開発中のみ）
				// 本番環境では削除してください
				setEvents([]);
			}
		};

		fetchEvents();
	}, [keyword, category, sort, page]);

	// 検索フォーム送信ハンドラ
	const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		const keyword = formData.get("keyword");

		setSearchParams((params) => {
			if (keyword) {
				params.set("keyword", keyword.toString());
			} else {
				params.delete("keyword");
			}

			// 検索時はページを1に戻す
			params.set("page", "1");
			return params;
		});
	};

	// カテゴリ変更ハンドラ
	const handleCategoryChange = (category: string) => {
		setSearchParams((params) => {
			if (category === "すべて") {
				params.delete("category");
			} else {
				params.set("category", category);
			}

			// カテゴリ変更時はページを1に戻す
			params.set("page", "1");
			return params;
		});
	};

	// ソート変更ハンドラ
	const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSearchParams((params) => {
			params.set("sort", e.target.value);
			return params;
		});
	};

	// ページ変更ハンドラ
	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set("page", page.toString());
			return params;
		});

		// ページトップにスクロール
		window.scrollTo(0, 0);
	};

	// イベントの最安チケット価格を取得
	const getMinPrice = (event: Event): number | null => {
		if (!event.ticket_types || event.ticket_types.length === 0) {
			return null;
		}

		const ticketPrices = event.ticket_types
			.filter((ticket) => !ticket.is_free)
			.map((ticket) => Math.round(ticket.price));

		return ticketPrices.length > 0 ? Math.min(...ticketPrices) : null;
	};

	return (
		<div className="space-y-8">
			{/* Search Section */}
			<section className="bg-gray-100 dark:bg-gray-800 -mx-4 px-4 py-8">
				<div className="max-w-4xl mx-auto">
					<form onSubmit={handleSearch} className="flex flex-col gap-4">
						<div className="flex gap-4">
							<input
								type="text"
								name="keyword"
								placeholder="イベント名、詳細、主催者名、会場名、住所で検索"
								className="flex-grow px-4 py-3 rounded-xl border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
								defaultValue={keyword}
							/>
							<button
								type="submit"
								className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl transition-all whitespace-nowrap"
							>
								検索
							</button>
						</div>
					</form>
				</div>
			</section>

			{/* Filter Section */}
			<section className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
				<div className="space-x-2 flex flex-wrap gap-2">
					{categories.map((cat) => (
						<button
							key={cat}
							onClick={() => handleCategoryChange(cat)}
							className={`px-3 sm:px-4 py-2 rounded-xl transition-all ${
								category === cat || (cat === "すべて" && !category)
									? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
									: "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-white"
							}`}
						>
							{cat}
						</button>
					))}
				</div>
				<select
					onChange={handleSortChange}
					value={sort}
					className="px-3 sm:px-4 py-2 rounded-xl border dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
				>
					{sortOptions.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</section>

			{/* Loading Indicator */}
			{isLoading && (
				<div className="text-center py-10">
					<p className="text-gray-600 dark:text-gray-300">
						イベント情報を読み込み中...
					</p>
				</div>
			)}

			{/* Error Message */}
			{error && !isLoading && (
				<div className="text-center py-10">
					<p className="text-red-600 dark:text-red-400">{error}</p>
					<button
						onClick={() => window.location.reload()}
						className="mt-4 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all"
					>
						再読み込み
					</button>
				</div>
			)}

			{/* No Results */}
			{!isLoading && !error && events.length === 0 && (
				<div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-2xl">
					<p className="text-gray-600 dark:text-gray-300">
						該当するイベントが見つかりませんでした
					</p>
					<button
						onClick={() => {
							setSearchParams({});
							window.location.reload();
						}}
						className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
					>
						すべてのイベントを表示
					</button>
				</div>
			)}

			{/* Events Grid */}
			{!isLoading && !error && events.length > 0 && (
				<section className="grid grid-cols-1 md:grid-cols-3 gap-6">
					{events.map((event) => {
						const minPrice = getMinPrice(event);
						const venueName =
							event.venue_name || `${event.prefecture} ${event.city}`;
						const formattedDate = formatJapaneseDateWithWeekday(event.date);

						return (
							<div
								key={event.id}
								className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all overflow-hidden border border-transparent dark:border-gray-700"
							>
								<div className="aspect-w-16 aspect-h-9">
									<img
										src={
											event.image_url || "https://via.placeholder.com/300x180"
										}
										alt={event.title}
										className="object-cover w-full h-full"
									/>
								</div>
								<div className="p-6">
									<h3 className="font-bold text-lg mb-2 dark:text-white">
										{event.title}
									</h3>
									<div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
										<p>{formattedDate}</p>
										{event.opening_times && event.opening_times.length > 0 ? (
											event.opening_times.length === 1 ? (
												<p>
													{event.opening_times[0].start_time.replace(
														/(\d+):(\d+).*/,
														"$1:$2"
													)}
													開演
												</p>
											) : (
												<div className="space-y-0.5">
													{event.opening_times.map((time, index) => (
														<p key={index}>
															{time.description || `第${index + 1}部`}:{" "}
															{time.start_time.replace(
																/(\d+):(\d+).*/,
																"$1:$2"
															)}
														</p>
													))}
												</div>
											)
										) : (
											<p>{formatJapaneseTime(event.time)}</p>
										)}
										<p>{venueName}</p>
										<p className="text-blue-600 dark:text-blue-400 font-semibold">
											{minPrice ? `¥${minPrice.toLocaleString()}〜` : "無料"}
										</p>
									</div>
									<Link
										to={`/events/${event.slug || event.id}`}
										className="mt-4 block w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-center px-4 py-3 rounded-xl transition-all"
									>
										詳細を見る
									</Link>
								</div>
							</div>
						);
					})}
				</section>
			)}

			{/* Pagination */}
			{!isLoading && !error && totalPages > 1 && (
				<section className="flex justify-center space-x-2">
					{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
						let pageNum: number;

						// 現在のページを中心に最大5ページ表示
						if (totalPages <= 5) {
							pageNum = i + 1;
						} else if (currentPage <= 3) {
							pageNum = i + 1;
						} else if (currentPage >= totalPages - 2) {
							pageNum = totalPages - 4 + i;
						} else {
							pageNum = currentPage - 2 + i;
						}

						return (
							<button
								key={pageNum}
								onClick={() => handlePageChange(pageNum)}
								className={`px-3 sm:px-4 py-2 rounded-xl transition-all ${
									pageNum === currentPage
										? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
										: "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-white"
								}`}
							>
								{pageNum}
							</button>
						);
					})}
				</section>
			)}
		</div>
	);
};

export default EventListPage;
